/**
 * "Where did the money go" for the ONE real wallet backing every deepseek-provider
 * account (deepseek_flash_ab_routing_test_2026_08_05 follow-up — operator ask after
 * the cross-attribution fix: "orchestrator and review agent spend should be tracked
 * separately but not as per-task breakdowns... the residual is human work outside AO").
 *
 * DeepSeek's API exposes only a remaining-balance snapshot, never spend history (see
 * server/deepseek_balance.py's own docstring) — the only way to know REAL total spend
 * is (every top-up ever recorded) minus (current balance), and the only way to know
 * every top-up is for the operator to record each one here. worker/orchestrator/review
 * spend is split from deepseek_message_usage by which slot produced each turn (slot 0
 * = orchestrator, configured review slots = review, everything else = worker); the
 * residual left after subtracting that attributed total from real wallet drawdown is
 * spend AO's own transcript sweep never saw at all (e.g. a manual `claude` session run
 * outside the fleet's slot-transcript directories).
 *
 * Self-contained fetch + poll, same shape as TaskUsageWindows.tsx: pure exported
 * formatters here (vitest-covered), the React shell only owns fetch/poll/form state.
 */

import { lazy, Suspense, useEffect, useRef, useState } from "react";

import { api } from "./api";
import { Panel } from "./components";
import type { DeepSeekWalletReconciliationView, DeepSeekWalletWindowReconciliationView } from "./types";

// Lazy: recharts (pulled in by UsageTimeSeriesModal) only loads once the operator
// actually opens the usage-over-time modal — see
// ao_control_plane_read_models_and_request_path_2026_09_04.md [UI] P0.
const UsageTimeSeriesModal = lazy(() =>
  import("./UsageTimeSeriesModal").then((m) => ({ default: m.UsageTimeSeriesModal })),
);

const POLL_INTERVAL_MS = 60000;

// The window endpoints (this file + Claude's) all share the same server-side
// cap (`Query(..., le=24 * 90)`) — 90 days is far beyond any provider's real sampling
// history today (all started within the last ~2 weeks), so requesting it returns
// everything ever sampled. Exported so Claude's panel uses the identical value
// rather than each re-deriving its own "lifetime" magic number.
export const LIFETIME_WINDOW_HOURS = 24 * 90;

// deepseek_wallet_residual_root_cause_and_windowed_reconciliation_2026_08_11 P2 —
// the windowed view's 1h/5h/24h/7d/30d/90d toggle (1h + Lifetime added 2026-08-18,
// operator ask to match TaskUsageWindows.tsx's window set on every wallet panel).
// hours is what the API call and the sampling-available-at math use; only the label
// differs between entries.
//
// The widest window is labelled "90d" (the server cap, NOT an unbounded all-time figure like the fleet table / TaskUsageWindows "Lifetime"; see ao_wallet_reconciliation_lifetime_window_mislabel_2026_09_30). It is the SAME formula as every
// other window, not a competing one: numerator = usage priced at the metered API rate,
// denominator = real $ actually spent (for a metered wallet like this one, that's the
// wallet-drawdown itself) — a window just widens how much history the delta covers.
// This is a genuinely different reading from the known-topup-total/opening-balance
// table above (which additionally separates the frozen pre-observability gap via a
// manually-recorded opening balance), so the two numbers can differ — that's expected,
// not a bug, since they answer "spend over the last 90 sampled days" vs "spend since a
// manually-frozen starting point," not the same question stated two ways.
const WINDOW_OPTIONS: { hours: number; label: string }[] = [
  { hours: 1, label: "1h" },
  { hours: 5, label: "5h" },
  { hours: 24, label: "24h" },
  { hours: 168, label: "7d" },
  { hours: 720, label: "30d" },
  { hours: LIFETIME_WINDOW_HOURS, label: "90d" },
];

// ── pure display mappers (vitest-covered) ──────────────────────────────────

/** "$12.3456" — "—" for null (nothing to divide/subtract against yet). */
export function fmtWalletUsdOrDash(amount: number | null): string {
  if (amount === null) return "—";
  return `$${amount.toFixed(4)}`;
}

/** Real spend AO's own transcript sweep never saw — negative would mean the
 * attributed total exceeds real wallet drawdown, which can only happen from a stale
 * balance reading or an unrecorded top-up; surfaced as-is (not clamped) so a bogus
 * negative is visible as a signal something's wrong, not silently hidden. */
export function residualLabel(residualUsd: number | null): string {
  if (residualUsd === null) return "Record a top-up to see this";
  if (residualUsd < 0) return "Unexpected negative — check for a missing top-up or stale balance";
  return "Human usage outside AO (manual chats etc.)";
}

/** Residual minus the frozen opening balance — the LIVE leak signal. A positive
 * number here is spend SINCE observability began that AO never attributed; negative
 * means the residual is now below the frozen baseline (possible once the phantom
 * top-up correction and real top-up recording catch up). Never clamped — a bogus
 * negative is as much a signal as a bogus positive. */
export function residualSinceObsLabel(residualSinceObs: number | null): string {
  if (residualSinceObs === null) return "Record the opening balance to separate live residual from history";
  if (residualSinceObs < 0) return "Below the frozen baseline — residual improved since observability began";
  return "Live residual since observability began (above the frozen pre-observability gap)";
}

/** "$70.0000 (sampled 2026-08-13T12:00:00Z)" — "—" when the series doesn't reach back
 * to this edge yet. Both sample timestamps (start AND end) are surfaced separately
 * rather than assumed to match the request, because the differenced span can skew a
 * bit either way (see DeepSeekWalletWindowReconciliation's SKEW note server-side). */
export function fmtWindowBalance(amount: number | null, sampledAt: string | null): string {
  if (amount === null || sampledAt === null) return "—";
  return `${fmtWalletUsdOrDash(amount)} (sampled ${sampledAt})`;
}

/** The real_spend_usd=None case must not render as a bare dash — a dash reads as zero,
 * not "unanswerable yet". None means the balance series doesn't reach back to this
 * window's start; anchor the "available at" math off series_starts_at (the OLDEST
 * sample), not window_start — that's the instant the window becomes answerable,
 * windowHours after the series began, regardless of how far in the past window_start
 * itself sits. null seriesStartsAt means no sample has EVER been recorded. */
export function windowedSpendCellText(
  realSpendUsd: number | null,
  seriesStartsAt: string | null,
  windowHours: number,
): string {
  if (realSpendUsd !== null) return fmtWalletUsdOrDash(realSpendUsd);
  if (seriesStartsAt === null) return "No balance samples recorded yet";
  const availableAt = new Date(new Date(seriesStartsAt).getTime() + windowHours * 3_600_000).toISOString();
  const label = WINDOW_OPTIONS.find((o) => o.hours === windowHours)?.label ?? `${windowHours}h`;
  return `sampling since ${seriesStartsAt} — ${label} view available at ${availableAt}`;
}

// ── component ────────────────────────────────────────────────────────────

export function DeepSeekWalletPanel({ baseUrl }: { baseUrl: string }) {
  const [usageTsOpen, setUsageTsOpen] = useState(false);
  const [data, setData] = useState<DeepSeekWalletReconciliationView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [amountInput, setAmountInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Opening-balance freeze (deepseek_wallet_residual_root_cause_and_windowed_
  // reconciliation_2026_08_11 P2): the measured pre-observability gap, recorded once
  // so the lifetime view can separate frozen history from live residual.
  const [openingInput, setOpeningInput] = useState("");
  const [openingNoteInput, setOpeningNoteInput] = useState("");
  const [submittingOpening, setSubmittingOpening] = useState(false);
  const [openingSubmitError, setOpeningSubmitError] = useState<string | null>(null);
  // 24h/7d windowed view (deepseek_wallet_residual_root_cause_and_windowed_
  // reconciliation_2026_08_11 P2) — a SEPARATE fetch/poll from the lifetime `data`
  // above: different endpoint, and re-fetches whenever the toggle selection changes.
  const [windowHours, setWindowHours] = useState(WINDOW_OPTIONS[0].hours);
  const [windowData, setWindowData] = useState<DeepSeekWalletWindowReconciliationView | null>(null);
  const [windowError, setWindowError] = useState<string | null>(null);
  // Both the poll and a form-submit POST write `data` from their own response. Without
  // this, a slow poll GET issued before a topup POST can resolve AFTER it and clobber
  // the fresher POST result with stale numbers — the id only lets the most-recently-
  // issued request's response commit, regardless of resolution order.
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const requestId = ++latestRequestIdRef.current;
      try {
        const result = await api.getDeepSeekWalletReconciliation(baseUrl);
        if (!cancelled && requestId === latestRequestIdRef.current) {
          setData(result);
          setError(null);
        }
      } catch (e) {
        if (!cancelled && requestId === latestRequestIdRef.current) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    };
    void load();
    const id = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl]);

  useEffect(() => {
    let cancelled = false;
    const loadWindow = async () => {
      try {
        const result = await api.getDeepSeekWalletWindowReconciliation(baseUrl, windowHours);
        if (!cancelled) {
          setWindowData(result);
          setWindowError(null);
        }
      } catch (e) {
        if (!cancelled) setWindowError(e instanceof Error ? e.message : String(e));
      }
    };
    void loadWindow();
    const id = setInterval(() => void loadWindow(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl, windowHours]);

  const handleRecordTopup = async () => {
    const amount = Number(amountInput);
    if (!Number.isFinite(amount) || amount <= 0) {
      setSubmitError("Enter a positive amount");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    const requestId = ++latestRequestIdRef.current;
    try {
      const result = await api.recordDeepSeekTopup(baseUrl, amount, noteInput || undefined);
      if (requestId === latestRequestIdRef.current) setData(result);
      setAmountInput("");
      setNoteInput("");
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordOpeningBalance = async () => {
    const amount = Number(openingInput);
    if (!Number.isFinite(amount) || amount < 0) {
      setOpeningSubmitError("Enter a non-negative amount");
      return;
    }
    setSubmittingOpening(true);
    setOpeningSubmitError(null);
    const requestId = ++latestRequestIdRef.current;
    try {
      const result = await api.recordDeepSeekOpeningBalance(baseUrl, amount, openingNoteInput || undefined);
      if (requestId === latestRequestIdRef.current) setData(result);
      setOpeningInput("");
      setOpeningNoteInput("");
    } catch (e) {
      setOpeningSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmittingOpening(false);
    }
  };

  return (
    <Panel
      title="DeepSeek Wallet Reconciliation"
      collapsible
      tone="accounts"
      right={
        <button className="btn btn-xs" onClick={() => setUsageTsOpen(true)}>
          Usage over time
        </button>
      }
    >
      {usageTsOpen && (
        <Suspense fallback={<div className="task-usage-pending">loading…</div>}>
          <UsageTimeSeriesModal
            baseUrl={baseUrl}
            open={usageTsOpen}
            onClose={() => setUsageTsOpen(false)}
            initialProvider="deepseek"
          />
        </Suspense>
      )}
      <div className="usage-panel-hint">
        Real wallet drawdown (recorded top-ups minus current balance) split into worker / orchestrator / review /
        scheduled-job / escalation-one-shot spend, with the leftover residual — spend AO never saw at all.
      </div>
      {error && <div className="task-usage-error">Failed to load: {error}</div>}
      {!error && !data && <div className="task-usage-pending">loading…</div>}
      {!error && data && (
        <>
          {/* Scoped container so e2e specs can target this table's "Residual"/"Attributed
              total" rows specifically — the windowed table below reuses those same
              row labels, and an unscoped `tr` text match would resolve to both. */}
          <div className="deepseek-wallet-lifetime">
            <table className="acc-usage-table acc-usage-table-breakdown">
              <tbody>
                <tr>
                  <td className="acc-usage-window-label">Known top-up total</td>
                  <td className="mono acc-usage-total" colSpan={2}>
                    {fmtWalletUsdOrDash(data.known_topup_total_usd)} ({data.topup_count} recorded)
                  </td>
                </tr>
                <tr>
                  <td className="acc-usage-window-label">Current balance</td>
                  <td className="mono" colSpan={2}>
                    {fmtWalletUsdOrDash(data.current_balance_usd)}
                  </td>
                </tr>
                <tr>
                  <td className="acc-usage-window-label">Real total spend</td>
                  <td className="mono" colSpan={2}>
                    {fmtWalletUsdOrDash(data.real_total_spend_usd)}
                  </td>
                </tr>
                <tr>
                  <td className="acc-usage-window-label">Worker (backlog tasks)</td>
                  <td className="mono" colSpan={2}>
                    {fmtWalletUsdOrDash(data.worker_spend_usd)}
                  </td>
                </tr>
                <tr>
                  <td className="acc-usage-window-label">Orchestrator (main agent)</td>
                  <td className="mono" colSpan={2}>
                    {fmtWalletUsdOrDash(data.orchestrator_spend_usd)}
                  </td>
                </tr>
                <tr>
                  <td className="acc-usage-window-label">Review agent</td>
                  <td className="mono" colSpan={2}>
                    {fmtWalletUsdOrDash(data.review_spend_usd)}
                  </td>
                </tr>
                <tr>
                  <td className="acc-usage-window-label">Scheduled jobs</td>
                  <td className="mono" colSpan={2}>
                    {fmtWalletUsdOrDash(data.scheduled_spend_usd)}
                  </td>
                </tr>
                <tr>
                  <td className="acc-usage-window-label">Escalation one-shots</td>
                  <td className="mono" colSpan={2}>
                    {fmtWalletUsdOrDash(data.escalation_spend_usd)}
                  </td>
                </tr>
                <tr>
                  <td className="acc-usage-window-label">Attributed total</td>
                  <td className="mono acc-usage-total" colSpan={2}>
                    {fmtWalletUsdOrDash(data.attributed_total_usd)}
                  </td>
                </tr>
                <tr>
                  <td className="acc-usage-window-label">Residual</td>
                  <td className="mono acc-usage-total" colSpan={2} title={residualLabel(data.residual_usd)}>
                    {fmtWalletUsdOrDash(data.residual_usd)}
                  </td>
                </tr>
                <tr>
                  <td className="acc-usage-window-label">Opening balance (frozen gap)</td>
                  <td className="mono" colSpan={2}>
                    {fmtWalletUsdOrDash(data.opening_balance_usd)}
                    {data.opening_balance_note ? (
                      <span className="acc-usage-note"> {data.opening_balance_note}</span>
                    ) : null}
                  </td>
                </tr>
                <tr>
                  <td className="acc-usage-window-label">Residual since observability</td>
                  <td
                    className="mono acc-usage-total"
                    colSpan={2}
                    title={residualSinceObsLabel(data.residual_since_observability_usd)}
                  >
                    {fmtWalletUsdOrDash(data.residual_since_observability_usd)}
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="usage-panel-hint">{residualLabel(data.residual_usd)}</div>
            <div className="usage-panel-hint">{residualSinceObsLabel(data.residual_since_observability_usd)}</div>
          </div>
          <div className="task-usage-provider-toggle">
            <input
              type="number"
              placeholder="Top-up amount, $"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              style={{ width: 110 }}
            />
            <input
              type="text"
              placeholder="Note (optional)"
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              style={{ width: 160 }}
            />
            <button className="btn btn-xs" onClick={() => void handleRecordTopup()} disabled={submitting}>
              {submitting ? "Recording…" : "Record top-up"}
            </button>
          </div>
          {submitError && <div className="task-usage-error">{submitError}</div>}
          <div className="task-usage-provider-toggle">
            <input
              type="number"
              placeholder="Opening balance, $"
              value={openingInput}
              onChange={(e) => setOpeningInput(e.target.value)}
              style={{ width: 130 }}
            />
            <input
              type="text"
              placeholder="Provenance (when/how measured)"
              value={openingNoteInput}
              onChange={(e) => setOpeningNoteInput(e.target.value)}
              style={{ width: 200 }}
            />
            <button
              className="btn btn-xs"
              onClick={() => void handleRecordOpeningBalance()}
              disabled={submittingOpening}
            >
              {submittingOpening ? "Freezing…" : "Freeze opening balance"}
            </button>
          </div>
          {openingSubmitError && <div className="task-usage-error">{openingSubmitError}</div>}
          <div className="usage-panel-hint">
            Windowed view: real wallet drawdown vs attributed spend bounded by two ACTUAL balance samples, so every
            dollar inside it has a transcript that still exists — a non-zero residual here is a live defect, not
            unattributable history.
          </div>
          <div className="task-usage-provider-toggle">
            {WINDOW_OPTIONS.map((opt) => (
              <button
                key={opt.hours}
                className={`btn btn-xs ${windowHours === opt.hours ? "active" : ""}`}
                onClick={() => setWindowHours(opt.hours)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {windowError && <div className="task-usage-error">Failed to load window: {windowError}</div>}
          {!windowError && !windowData && <div className="task-usage-pending">loading…</div>}
          {!windowError && windowData && (
            <div className="deepseek-wallet-window">
              <table className="acc-usage-table acc-usage-table-breakdown">
                <tbody>
                  <tr>
                    <td className="acc-usage-window-label">Window</td>
                    <td className="mono" colSpan={2}>
                      {windowData.window_start} — {windowData.window_end}
                    </td>
                  </tr>
                  <tr>
                    <td className="acc-usage-window-label">Balance at start</td>
                    <td className="mono" colSpan={2}>
                      {fmtWindowBalance(windowData.balance_at_start_usd, windowData.balance_at_start_sampled_at)}
                    </td>
                  </tr>
                  <tr>
                    <td className="acc-usage-window-label">Balance at end</td>
                    <td className="mono" colSpan={2}>
                      {fmtWindowBalance(windowData.balance_at_end_usd, windowData.balance_at_end_sampled_at)}
                    </td>
                  </tr>
                  <tr>
                    <td className="acc-usage-window-label">Top-ups in window</td>
                    <td className="mono" colSpan={2}>
                      {fmtWalletUsdOrDash(windowData.topups_in_window_usd)}
                    </td>
                  </tr>
                  <tr>
                    <td className="acc-usage-window-label">Real spend</td>
                    <td className="mono acc-usage-total" colSpan={2}>
                      {windowedSpendCellText(windowData.real_spend_usd, windowData.series_starts_at, windowHours)}
                    </td>
                  </tr>
                  <tr>
                    <td className="acc-usage-window-label">Attributed total</td>
                    <td className="mono acc-usage-total" colSpan={2}>
                      {fmtWalletUsdOrDash(windowData.attributed_total_usd)}
                    </td>
                  </tr>
                  <tr>
                    <td className="acc-usage-window-label">Residual</td>
                    <td className="mono acc-usage-total" colSpan={2}>
                      {windowData.residual_usd === null
                        ? windowedSpendCellText(null, windowData.series_starts_at, windowHours)
                        : fmtWalletUsdOrDash(windowData.residual_usd)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
